import React, { useState, useEffect } from 'react';
import { paymentsService } from '../../services/paymentsService';
import { paymentLinksService } from '../../services/paymentLinksService';
import { SingleCardLogo } from '../Common/CreditCardLogos';
import { detectCardType } from '../../utils/cardDetection';
import { useResponsive } from '../../hooks/useResponsive';
import travelieTheme from '../../styles/travelieTheme';
import './SecurePaymentDetailsModal.css';

// Phase 5.4: Migrated to TravelieTheme - June 16, 2026

const SecurePaymentDetailsModal = ({ 
  isOpen, 
  onClose, 
  quote,
  itinerary,
  onPaymentDeleted,
  isPaymentLink = false,
  paymentLinkId = null
}) => {
  const { isMobile } = useResponsive();
  const [step, setStep] = useState('request'); // 'select', 'request', 'verify', 'details', 'confirm-delete'
  const [method, setMethod] = useState('email');
  const [tempToken, setTempToken] = useState('');
  const [code, setCode] = useState('');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);

  // Helper function for button styling
  const getButtonStyle = (type) => {
    const baseStyle = {
      padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
      fontSize: travelieTheme.typography.fontSize.sm,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      borderRadius: travelieTheme.components.card.borderRadius,
      border: 'none',
      cursor: 'pointer',
      transition: `all ${travelieTheme.transitions.fast}`,
      whiteSpace: 'nowrap'
    };

    const styles = {
      primary: {
        ...baseStyle,
        background: travelieTheme.colors.primary.main,
        color: '#FFFFFF'
      },
      secondary: {
        ...baseStyle,
        background: travelieTheme.colors.neutral.border,
        color: travelieTheme.colors.text.primary
      },
      danger: {
        ...baseStyle,
        background: travelieTheme.colors.status.error,
        color: '#FFFFFF'
      }
    };

    return styles[type] || styles.primary;
  };

  // Get all payment IDs from either quote or itinerary
  const sourceData = quote || itinerary;
  const paymentIds = sourceData?.payment_ids || [];
  const paymentCount = sourceData?.payment_count || 0;
  
  // Auto-select first payment if only one exists, otherwise show selection
  const paymentId = selectedPaymentId || (paymentIds.length === 1 ? paymentIds[0] : null);

  // Set initial step based on number of payments
  useEffect(() => {
    if (isOpen && paymentIds.length > 1) {
      setStep('select');
    } else if (isOpen && paymentIds.length === 1) {
      setStep('request');
    }
  }, [isOpen, paymentIds.length]);

  const handleClose = () => {
    setStep(paymentIds.length > 1 ? 'select' : 'request');
    setMethod('email');
    setTempToken('');
    setCode('');
    setPaymentDetails(null);
    setSelectedPaymentId(null);
    setError('');
    onClose();
  };

  const handlePaymentSelect = (paymentId) => {
    setSelectedPaymentId(paymentId);
    setStep('request');
    setError('');
  };

  const requestAccess = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await paymentsService.requestPaymentAccess(paymentId, method);
      setTempToken(response.tempToken);
      setStep('verify');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to request access');
    } finally {
      setLoading(false);
    }
  };

  const verifyAccess = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await paymentsService.verifyPaymentAccess(paymentId, tempToken, code);
      setPaymentDetails(response);
      setStep('details');
    } catch (error) {
      setError(error.response?.data?.error || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setStep('confirm-delete');
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (isPaymentLink && paymentLinkId) {
        // For Payment Links, delete the card details only (a completed link stays Completed)
        await paymentLinksService.cancel(paymentLinkId);
        
        if (onPaymentDeleted) {
          onPaymentDeleted(paymentLinkId);
        }
      } else {
        // For regular payments, delete the payment
        await paymentsService.deletePayment(paymentId);
        
        if (onPaymentDeleted) {
          onPaymentDeleted(paymentId);
        }
      }
      
      handleClose();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete payment');
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setStep('details');
  };

  if (!isOpen) return null;

  // Safety check - don't render if no payments exist at all
  if (paymentIds.length === 0) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: travelieTheme.spacing.md
      }} onClick={handleClose}>
        <div style={{
          background: travelieTheme.colors.neutral.card,
          borderRadius: travelieTheme.components.card.borderRadius,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          maxWidth: '500px',
          width: '100%'
        }} onClick={(e) => e.stopPropagation()}>
          <div style={{
            padding: travelieTheme.spacing.lg,
            borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{
              fontSize: travelieTheme.typography.fontSize.xl,
              fontWeight: travelieTheme.typography.fontWeight.semibold,
              color: travelieTheme.colors.text.primary,
              margin: 0
            }}>No Payment Found</h2>
            <button onClick={handleClose} style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: travelieTheme.colors.text.secondary,
              cursor: 'pointer',
              padding: travelieTheme.spacing.xs,
              lineHeight: 1
            }}>&times;</button>
          </div>
          <div style={{ padding: travelieTheme.spacing.lg }}>
            <p style={{
              fontSize: travelieTheme.typography.fontSize.sm,
              color: travelieTheme.colors.text.secondary,
              marginBottom: travelieTheme.spacing.lg
            }}>No payment information found for this quote or itinerary.</p>
            <button 
              onClick={handleClose}
              style={getButtonStyle('secondary')}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: travelieTheme.spacing.md
    }} onClick={handleClose}>
      <div style={{
        background: travelieTheme.colors.neutral.card,
        borderRadius: travelieTheme.components.card.borderRadius,
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        maxWidth: isMobile ? '100%' : '700px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{
          padding: travelieTheme.spacing.lg,
          borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0
        }}>
          <h2 style={{
            fontSize: travelieTheme.typography.fontSize.xl,
            fontWeight: travelieTheme.typography.fontWeight.semibold,
            color: travelieTheme.colors.text.primary,
            margin: 0
          }}>
            {step === 'select' && `Select Payment (${paymentCount} payments)`}
            {step === 'request' && 'Secure Payment Access'}
            {step === 'verify' && 'Enter Verification Code'}
            {step === 'details' && 'Payment Details'}
            {step === 'confirm-delete' && 'Confirm Deletion'}
          </h2>
          <button onClick={handleClose} style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            color: travelieTheme.colors.text.secondary,
            cursor: 'pointer',
            padding: travelieTheme.spacing.xs,
            lineHeight: 1,
            transition: `color ${travelieTheme.transitions.fast}`
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = travelieTheme.colors.text.primary}
          onMouseLeave={(e) => e.currentTarget.style.color = travelieTheme.colors.text.secondary}
          >&times;</button>
        </div>

        <div style={{
          padding: travelieTheme.spacing.lg,
          flex: 1,
          overflow: 'auto'
        }}>
          {error && (
            <div style={{
              marginBottom: travelieTheme.spacing.md,
              padding: travelieTheme.spacing.md,
              background: '#FEF2F2',
              border: `1px solid ${travelieTheme.colors.status.error}`,
              borderRadius: travelieTheme.components.card.borderRadius,
              color: travelieTheme.colors.status.error,
              fontSize: travelieTheme.typography.fontSize.sm
            }}>
              {error}
            </div>
          )}

          {step === 'select' && (
            <div>
              <p style={{
                fontSize: travelieTheme.typography.fontSize.sm,
                color: travelieTheme.colors.text.secondary,
                marginBottom: travelieTheme.spacing.lg
              }}>This {quote ? 'quote' : 'itinerary'} has {paymentCount} payment submissions. Please select which payment you'd like to view:</p>
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: travelieTheme.spacing.sm
              }}>
                {paymentIds.map((id, index) => (
                  <button 
                    key={id}
                    onClick={() => handlePaymentSelect(id)}
                    style={{
                      ...getButtonStyle('primary'),
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      padding: travelieTheme.spacing.md
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    <div style={{ textAlign: 'left' }}>
                      <div style={{
                        fontWeight: travelieTheme.typography.fontWeight.semibold,
                        marginBottom: '4px'
                      }}>Payment #{index + 1}</div>
                      <div style={{
                        fontSize: travelieTheme.typography.fontSize.xs,
                        opacity: 0.8
                      }}>ID: {id}</div>
                    </div>
                    <div style={{ fontSize: '20px' }}>→</div>
                  </button>
                ))}
              </div>
              
              <div style={{
                marginTop: travelieTheme.spacing.lg,
                padding: travelieTheme.spacing.md,
                background: '#EFF6FF',
                border: `1px solid ${travelieTheme.colors.primary.main}`,
                borderRadius: travelieTheme.components.card.borderRadius,
                fontSize: travelieTheme.typography.fontSize.sm,
                color: travelieTheme.colors.text.secondary
              }}>
                <p style={{ margin: 0 }}><strong>Note:</strong> Each payment submission may contain different payment methods or amounts. Select the payment you want to review.</p>
              </div>
            </div>
          )}

          {step === 'request' && (
            <div>
              <p style={{
                fontSize: travelieTheme.typography.fontSize.sm,
                color: travelieTheme.colors.text.secondary,
                marginBottom: travelieTheme.spacing.lg
              }}>To view payment details, please verify your identity using two-factor authentication.</p>
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: travelieTheme.spacing.sm,
                marginBottom: travelieTheme.spacing.xl
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: travelieTheme.spacing.sm,
                  padding: travelieTheme.spacing.md,
                  border: `2px solid ${method === 'email' ? travelieTheme.colors.primary.main : travelieTheme.colors.neutral.border}`,
                  borderRadius: travelieTheme.components.card.borderRadius,
                  cursor: 'pointer',
                  transition: `all ${travelieTheme.transitions.fast}`,
                  background: method === 'email' ? '#EFF6FF' : 'transparent'
                }}>
                  <input
                    type="radio"
                    value="email"
                    checked={method === 'email'}
                    onChange={(e) => setMethod(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{
                    fontSize: travelieTheme.typography.fontSize.sm,
                    fontWeight: method === 'email' ? travelieTheme.typography.fontWeight.medium : 'normal'
                  }}>Send code via Email</span>
                </label>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: travelieTheme.spacing.sm,
                  padding: travelieTheme.spacing.md,
                  border: `2px solid ${method === 'sms' ? travelieTheme.colors.primary.main : travelieTheme.colors.neutral.border}`,
                  borderRadius: travelieTheme.components.card.borderRadius,
                  cursor: 'pointer',
                  transition: `all ${travelieTheme.transitions.fast}`,
                  background: method === 'sms' ? '#EFF6FF' : 'transparent'
                }}>
                  <input
                    type="radio"
                    value="sms"
                    checked={method === 'sms'}
                    onChange={(e) => setMethod(e.target.value)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{
                    fontSize: travelieTheme.typography.fontSize.sm,
                    fontWeight: method === 'sms' ? travelieTheme.typography.fontWeight.medium : 'normal'
                  }}>Send code via SMS</span>
                </label>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: paymentIds.length > 1 ? 'space-between' : 'flex-end',
                gap: travelieTheme.spacing.sm,
                paddingTop: travelieTheme.spacing.md,
                borderTop: `1px solid ${travelieTheme.colors.neutral.border}`
              }}>
                {paymentIds.length > 1 && (
                  <button 
                    onClick={() => setStep('select')}
                    style={getButtonStyle('secondary')}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    ← Back to Selection
                  </button>
                )}
                <button 
                  onClick={requestAccess}
                  disabled={loading}
                  style={{
                    ...getButtonStyle('primary'),
                    opacity: loading ? '0.5' : '1',
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) e.currentTarget.style.opacity = '0.9';
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) e.currentTarget.style.opacity = '1';
                  }}
                >
                  {loading ? 'Sending...' : 'Send Verification Code'}
                </button>
              </div>
            </div>
          )}

          {step === 'verify' && (
            <div>
              <p style={{
                fontSize: travelieTheme.typography.fontSize.sm,
                color: travelieTheme.colors.text.secondary,
                marginBottom: travelieTheme.spacing.lg
              }}>Enter the 6-digit verification code sent to your {method}:</p>
              
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength="6"
                style={{
                  width: '100%',
                  padding: travelieTheme.spacing.md,
                  fontSize: '24px',
                  fontWeight: travelieTheme.typography.fontWeight.semibold,
                  textAlign: 'center',
                  letterSpacing: '8px',
                  border: `2px solid ${travelieTheme.colors.neutral.border}`,
                  borderRadius: travelieTheme.components.card.borderRadius,
                  fontFamily: 'monospace',
                  outline: 'none',
                  transition: `border-color ${travelieTheme.transitions.fast}`,
                  marginBottom: travelieTheme.spacing.xl
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = travelieTheme.colors.primary.main}
                onBlur={(e) => e.currentTarget.style.borderColor = travelieTheme.colors.neutral.border}
              />

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: travelieTheme.spacing.sm,
                paddingTop: travelieTheme.spacing.md,
                borderTop: `1px solid ${travelieTheme.colors.neutral.border}`
              }}>
                <button 
                  onClick={() => setStep('request')}
                  style={getButtonStyle('secondary')}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  ← Back
                </button>
                <button 
                  onClick={verifyAccess}
                  disabled={loading || code.length !== 6}
                  style={{
                    ...getButtonStyle('primary'),
                    opacity: (loading || code.length !== 6) ? '0.5' : '1',
                    cursor: (loading || code.length !== 6) ? 'not-allowed' : 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading && code.length === 6) e.currentTarget.style.opacity = '0.9';
                  }}
                  onMouseLeave={(e) => {
                    if (!loading && code.length === 6) e.currentTarget.style.opacity = '1';
                  }}
                >
                  {loading ? 'Verifying...' : 'Verify Code'}
                </button>
              </div>
            </div>
          )}

          {step === 'details' && paymentDetails && (
            <div className="payment-details">
              <div className="security-notice">
                <div className="security-icon">🔒</div>
                <p>This information is protected by two-factor authentication and is for agent use only.</p>
              </div>
              {paymentDetails.cardDataPurged && (
                <div className="security-notice">
                  <div className="security-icon">🗑️</div>
                  <p>{paymentDetails.cardDataPurgedMessage || 'This information is no longer available as travel has completed.'}</p>
                </div>
              )}

              <div className="details-grid">
                {/* Primary Payment Information */}
                <div className="detail-section">
                  <h4>Payment Information</h4>
                  <div className="detail-item">
                    <label>Payment Amount:</label>
                    <span className="amount">${parseFloat(paymentDetails.amount || 0).toFixed(2)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Payment Type:</label>
                    <span className="payment-type-with-logo">
                      {paymentDetails.payment_type || 'N/A'}
                      {paymentDetails.payment_data?.primaryCard?.cardNumber && (
                        <SingleCardLogo 
                          cardType={detectCardType(paymentDetails.payment_data.primaryCard.cardNumber)} 
                          size="medium" 
                        />
                      )}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Payment Method:</label>
                    <span>{paymentDetails.payment_method || 'Credit Card'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <span className={`status ${paymentDetails.status}`}>{paymentDetails.status || 'N/A'}</span>
                  </div>
                  {paymentDetails.confirmation_number && (
                    <div className="detail-item">
                      <label>Confirmation Number:</label>
                      <span>{paymentDetails.confirmation_number}</span>
                    </div>
                  )}
                  {paymentDetails.transaction_id && (
                    <div className="detail-item">
                      <label>Transaction ID:</label>
                      <span>{paymentDetails.transaction_id}</span>
                    </div>
                  )}
                </div>

                {/* Primary Credit Card Information */}
                <div className="detail-section">
                  <h4>Primary Card Information</h4>
                  {paymentDetails.payment_data?.primaryCard?.cardName && (
                    <div className="detail-item">
                      <label>Name on Card:</label>
                      <span>{paymentDetails.payment_data.primaryCard.cardName}</span>
                    </div>
                  )}
                  
                  {/* Full Credit Card Number */}
                  {paymentDetails.payment_data?.primaryCard?.cardNumber && (
                    <div className="detail-item sensitive">
                      <label>Credit Card Number:</label>
                      <span className="card-number">
                        {paymentDetails.payment_data.primaryCard.cardNumber}
                        <SingleCardLogo 
                          cardType={detectCardType(paymentDetails.payment_data.primaryCard.cardNumber)} 
                          size="small" 
                        />
                      </span>
                    </div>
                  )}
                  
                  {/* If full card number not available, show what we have */}
                  {!paymentDetails.payment_data?.primaryCard?.cardNumber && paymentDetails.card_last_four && (
                    <div className="detail-item">
                      <label>Card Last Four:</label>
                      <span className="card-number">
                        ****-****-****-{paymentDetails.card_last_four}
                        {/* Try to detect card type from payment method or other data */}
                        <SingleCardLogo 
                          cardType={detectCardType(`4${paymentDetails.card_last_four}`)} // Assume Visa for demo
                          size="small" 
                        />
                      </span>
                    </div>
                  )}
                  
                  {/* Card Expiration */}
                  {paymentDetails.payment_data?.primaryCard?.expirationMonth && paymentDetails.payment_data?.primaryCard?.expirationYear && (
                    <div className="detail-item sensitive">
                      <label>Expiration:</label>
                      <span>{paymentDetails.payment_data.primaryCard.expirationMonth.toString().padStart(2, '0')}/{paymentDetails.payment_data.primaryCard.expirationYear}</span>
                    </div>
                  )}
                  
                  {/* CVV */}
                  {paymentDetails.payment_data?.primaryCard?.cvv && (
                    <div className="detail-item sensitive">
                      <label>CVV:</label>
                      <span>{paymentDetails.payment_data.primaryCard.cvv}</span>
                    </div>
                  )}
                  
                  {/* Card Type/Brand */}
                  {paymentDetails.payment_data?.primaryCard?.cardBrand && (
                    <div className="detail-item">
                      <label>Card Type:</label>
                      <span>{paymentDetails.payment_data.primaryCard.cardBrand}</span>
                    </div>
                  )}
                  
                  {/* Billing Address */}
                  {paymentDetails.payment_data?.primaryCard?.billingAddress && (
                    <div className="detail-item address-item">
                      <label>Billing Address:</label>
                      <div className="address-details">
                        <div>{paymentDetails.payment_data.primaryCard.billingAddress.address}</div>
                        <div>{paymentDetails.payment_data.primaryCard.billingAddress.city}, {paymentDetails.payment_data.primaryCard.billingAddress.state} {paymentDetails.payment_data.primaryCard.billingAddress.zip}</div>
                        <div>{paymentDetails.payment_data.primaryCard.billingAddress.country}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Contact Information */}
                {paymentDetails.payment_data?.contact && (
                  <div className="detail-section">
                    <h4>Contact Information</h4>
                    {paymentDetails.payment_data.contact.email && (
                      <div className="detail-item">
                        <label>Email:</label>
                        <span>{paymentDetails.payment_data.contact.email}</span>
                      </div>
                    )}
                    {paymentDetails.payment_data.contact.phone && (
                      <div className="detail-item">
                        <label>Phone:</label>
                        <span>{paymentDetails.payment_data.contact.phone}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Secondary Payment Information */}
                {paymentDetails.payment_data?.secondaryCard && (
                  <div className="detail-section secondary-payment">
                    <h4>Secondary Card Information</h4>
                    {paymentDetails.payment_data.secondaryCard.cardholderName && (
                      <div className="detail-item">
                        <label>Name on Card:</label>
                        <span>{paymentDetails.payment_data.secondaryCard.cardholderName}</span>
                      </div>
                    )}
                    
                    {paymentDetails.payment_data.secondaryCard.cardNumber && (
                      <div className="detail-item sensitive">
                        <label>Credit Card Number:</label>
                        <span className="card-number">{paymentDetails.payment_data.secondaryCard.cardNumber}</span>
                      </div>
                    )}
                    
                    {paymentDetails.payment_data.secondaryCard.expirationMonth && paymentDetails.payment_data.secondaryCard.expirationYear && (
                      <div className="detail-item sensitive">
                        <label>Expiration:</label>
                        <span>{paymentDetails.payment_data.secondaryCard.expirationMonth.toString().padStart(2, '0')}/{paymentDetails.payment_data.secondaryCard.expirationYear}</span>
                      </div>
                    )}
                    
                    {paymentDetails.payment_data.secondaryCard.cvv && (
                      <div className="detail-item sensitive">
                        <label>CVV:</label>
                        <span>{paymentDetails.payment_data.secondaryCard.cvv}</span>
                      </div>
                    )}
                    
                    {paymentDetails.payment_data.secondaryCard.amount && (
                      <div className="detail-item">
                        <label>Secondary Payment Amount:</label>
                        <span className="amount">${parseFloat(paymentDetails.payment_data.secondaryCard.amount).toFixed(2)}</span>
                      </div>
                    )}
                    
                    {paymentDetails.payment_data.secondaryCard.cardBrand && (
                      <div className="detail-item">
                        <label>Card Type:</label>
                        <span>{paymentDetails.payment_data.secondaryCard.cardBrand}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Gift Card Information */}
                {paymentDetails.payment_data?.giftCard && (
                  <div className="detail-section gift-card-section">
                    <h4>🎁 Gift Card Information</h4>
                    
                    {paymentDetails.payment_data.giftCard.cardType && (
                      <div className="detail-item">
                        <label>Card Type:</label>
                        <span>{paymentDetails.payment_data.giftCard.cardType}</span>
                      </div>
                    )}
                    
                    {/* Full Gift Card Number */}
                    {paymentDetails.payment_data.giftCard.cardNumber && (
                      <div className="detail-item sensitive">
                        <label>Gift Card Number:</label>
                        <span className="card-number gift-card-number">
                          {paymentDetails.payment_data.giftCard.cardNumber}
                        </span>
                      </div>
                    )}
                    
                    {/* PIN/Security Code */}
                    {paymentDetails.payment_data.giftCard.pin && (
                      <div className="detail-item sensitive">
                        <label>PIN/Security Code:</label>
                        <span>{paymentDetails.payment_data.giftCard.pin}</span>
                      </div>
                    )}
                    
                    {/* Amount (if splitting payment) */}
                    {paymentDetails.payment_data.giftCard.amount && (
                      <div className="detail-item">
                        <label>Gift Card Amount:</label>
                        <span className="amount">${parseFloat(paymentDetails.payment_data.giftCard.amount).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Client Information */}
                {(paymentDetails.first_name || paymentDetails.last_name || paymentDetails.email) && (
                  <div className="detail-section">
                    <h4>Client Information</h4>
                    {(paymentDetails.first_name || paymentDetails.last_name) && (
                      <div className="detail-item">
                        <label>Client Name:</label>
                        <span>{`${paymentDetails.first_name || ''} ${paymentDetails.last_name || ''}`.trim()}</span>
                      </div>
                    )}
                    {paymentDetails.email && (
                      <div className="detail-item">
                        <label>Client Email:</label>
                        <span>{paymentDetails.email}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Quote/Itinerary Information */}
                {(paymentDetails.quote_title || paymentDetails.quote_number) && (
                  <div className="detail-section">
                    <h4>Related Quote/Itinerary</h4>
                    {paymentDetails.quote_title && (
                      <div className="detail-item">
                        <label>Title:</label>
                        <span>{paymentDetails.quote_title}</span>
                      </div>
                    )}
                    {paymentDetails.quote_number && (
                      <div className="detail-item">
                        <label>Quote Number:</label>
                        <span>{paymentDetails.quote_number}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Additional Payment Data */}
                {paymentDetails.payment_data && Object.keys(paymentDetails.payment_data).length > 0 && (
                  <div className="detail-section full-width">
                    <h4>Additional Information</h4>
                    {paymentDetails.payment_data.agreeToTerms && (
                      <div className="detail-item">
                        <label>Terms Agreed:</label>
                        <span>✓ Yes</span>
                      </div>
                    )}
                    {paymentDetails.processed_at && (
                      <div className="detail-item">
                        <label>Processed At:</label>
                        <span>{new Date(paymentDetails.processed_at).toLocaleString()}</span>
                      </div>
                    )}
                    {paymentDetails.created_at && (
                      <div className="detail-item">
                        <label>Created At:</label>
                        <span>{new Date(paymentDetails.created_at).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: travelieTheme.spacing.sm,
                paddingTop: travelieTheme.spacing.lg,
                marginTop: travelieTheme.spacing.lg,
                borderTop: `1px solid ${travelieTheme.colors.neutral.border}`
              }}>
                <button 
                  onClick={handleDeleteClick}
                  style={getButtonStyle('danger')}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  {isPaymentLink && quote?.paymentLinkStatus === 'completed' 
                    ? '🔒 Delete Card Details (Security)' 
                    : 'Delete Payment'}
                </button>
                <button 
                  onClick={handleClose}
                  style={getButtonStyle('secondary')}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {step === 'confirm-delete' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '48px',
                marginBottom: travelieTheme.spacing.md
              }}>⚠️</div>
              <h3 style={{
                fontSize: travelieTheme.typography.fontSize.lg,
                fontWeight: travelieTheme.typography.fontWeight.semibold,
                color: travelieTheme.colors.text.primary,
                marginBottom: travelieTheme.spacing.sm
              }}>{isPaymentLink ? 'Are you sure you want to delete the card details?' : 'Are you sure you want to delete this Payment Information?'}</h3>
              <p style={{
                fontSize: travelieTheme.typography.fontSize.sm,
                color: travelieTheme.colors.text.secondary,
                marginBottom: travelieTheme.spacing.lg
              }}>{isPaymentLink ? 'The card details will be permanently deleted and cannot be recovered. The payment link will stay Completed, and the payment record (amount and confirmation number) will be kept.' : 'This action cannot be undone. The payment record will be permanently removed from the system.'}</p>
              
              {paymentDetails && (
                <div style={{
                  padding: travelieTheme.spacing.md,
                  background: '#FEF2F2',
                  border: `1px solid ${travelieTheme.colors.status.error}`,
                  borderRadius: travelieTheme.components.card.borderRadius,
                  marginBottom: travelieTheme.spacing.xl,
                  textAlign: 'left'
                }}>
                  <p style={{
                    fontSize: travelieTheme.typography.fontSize.sm,
                    marginBottom: travelieTheme.spacing.xs
                  }}><strong>Payment ID:</strong> {paymentDetails.id}</p>
                  <p style={{
                    fontSize: travelieTheme.typography.fontSize.sm,
                    marginBottom: travelieTheme.spacing.xs
                  }}><strong>Amount:</strong> ${parseFloat(paymentDetails.amount || 0).toFixed(2)}</p>
                  <p style={{
                    fontSize: travelieTheme.typography.fontSize.sm,
                    margin: 0
                  }}><strong>Quote/Itinerary:</strong> {paymentDetails.quote_title}</p>
                </div>
              )}

              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: travelieTheme.spacing.sm,
                paddingTop: travelieTheme.spacing.md,
                borderTop: `1px solid ${travelieTheme.colors.neutral.border}`
              }}>
                <button 
                  onClick={cancelDelete}
                  disabled={loading}
                  style={{
                    ...getButtonStyle('secondary'),
                    opacity: loading ? '0.5' : '1',
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) e.currentTarget.style.opacity = '0.9';
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) e.currentTarget.style.opacity = '1';
                  }}
                >
                  No, Keep Payment
                </button>
                <button 
                  onClick={confirmDelete}
                  disabled={loading}
                  style={{
                    ...getButtonStyle('danger'),
                    opacity: loading ? '0.5' : '1',
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) e.currentTarget.style.opacity = '0.9';
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) e.currentTarget.style.opacity = '1';
                  }}
                >
                  {loading ? 'Deleting...' : (isPaymentLink ? 'Yes, Delete Card Details' : 'Yes, Delete Payment')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecurePaymentDetailsModal;
